/**
 * Asynchronous control-flow helpers: delays, retries with backoff, timeouts,
 * and bounded-concurrency mapping. All helpers are isomorphic (no Node-only
 * APIs) and honour `AbortSignal` where it makes sense.
 */

/** Reusable abort-aware delay error. */
export class AbortError extends Error {
  constructor(message = 'The operation was aborted') {
    super(message);
    this.name = 'AbortError';
  }
}

/** Options for {@link sleep}. */
export interface SleepOptions {
  /** Abort the pending delay early, rejecting with {@link AbortError}. */
  readonly signal?: AbortSignal;
}

/**
 * Resolve after `ms` milliseconds. When an aborted `signal` is supplied the
 * returned promise rejects immediately (or as soon as the signal fires).
 */
export function sleep(ms: number, options: SleepOptions = {}): Promise<void> {
  const { signal } = options;
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(toAbortError(signal));
      return;
    }

    const timer = setTimeout(() => {
      cleanup();
      resolve();
    }, Math.max(0, ms));

    const onAbort = (): void => {
      cleanup();
      reject(toAbortError(signal));
    };

    const cleanup = (): void => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
    };

    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** Configuration for {@link retry}. */
export interface RetryOptions {
  /** Maximum number of retry attempts after the initial try. Default `3`. */
  readonly retries?: number;
  /** Base delay in milliseconds between attempts. Default `200`. */
  readonly delay?: number;
  /**
   * Backoff strategy. `exponential` doubles the delay each attempt (with full
   * jitter), `linear` multiplies by attempt index, `fixed` keeps `delay`
   * constant. Default `exponential`.
   */
  readonly backoff?: 'fixed' | 'linear' | 'exponential';
  /** Upper bound applied to any computed delay, in milliseconds. */
  readonly maxDelay?: number;
  /** Predicate deciding whether a given error is retryable. Default: always. */
  readonly shouldRetry?: (error: unknown, attempt: number) => boolean;
  /** Invoked before each backoff wait, for logging/telemetry. */
  readonly onRetry?: (error: unknown, attempt: number, waitMs: number) => void;
  /** Abort all pending retries. */
  readonly signal?: AbortSignal;
}

/**
 * Invoke `fn`, retrying on rejection according to {@link RetryOptions}.
 *
 * The function receives the 1-based attempt number. The final error is rethrown
 * once all retries are exhausted or `shouldRetry` returns `false`.
 */
export async function retry<T>(fn: (attempt: number) => Promise<T> | T, options: RetryOptions = {}): Promise<T> {
  const {
    retries = 3,
    delay = 200,
    backoff = 'exponential',
    maxDelay = 30_000,
    shouldRetry = () => true,
    onRetry,
    signal,
  } = options;

  let lastError: unknown;

  for (let attempt = 1; attempt <= retries + 1; attempt += 1) {
    if (signal?.aborted) throw toAbortError(signal);
    try {
      return await fn(attempt);
    } catch (error) {
      lastError = error;
      const isLastAttempt = attempt > retries;
      if (isLastAttempt || !shouldRetry(error, attempt)) {
        throw error;
      }
      const waitMs = Math.min(maxDelay, computeBackoff(backoff, delay, attempt));
      onRetry?.(error, attempt, waitMs);
      await sleep(waitMs, signal ? { signal } : {});
    }
  }

  // Unreachable in practice; satisfies the type checker.
  throw lastError;
}

function computeBackoff(strategy: NonNullable<RetryOptions['backoff']>, base: number, attempt: number): number {
  switch (strategy) {
    case 'fixed':
      return base;
    case 'linear':
      return base * attempt;
    case 'exponential': {
      const ceiling = base * 2 ** (attempt - 1);
      // Full jitter to avoid thundering-herd retries.
      return Math.random() * ceiling;
    }
    default:
      return base;
  }
}

/** Options for {@link withTimeout}. */
export interface TimeoutOptions {
  /** Error thrown when the timeout elapses. Defaults to a generic timeout error. */
  readonly error?: Error;
  /** Propagate cancellation to the wrapped operation if it observes the signal. */
  readonly signal?: AbortSignal;
}

/** Error thrown by {@link withTimeout} when the deadline is exceeded. */
export class TimeoutError extends Error {
  constructor(message = 'Operation timed out') {
    super(message);
    this.name = 'TimeoutError';
  }
}

/**
 * Race a promise against a timeout.
 *
 * Accepts either a promise or a factory that receives an `AbortSignal` so the
 * underlying work can be cancelled when the deadline fires.
 */
export function withTimeout<T>(
  input: Promise<T> | ((signal: AbortSignal) => Promise<T>),
  ms: number,
  options: TimeoutOptions = {},
): Promise<T> {
  const controller = new AbortController();
  if (options.signal) {
    if (options.signal.aborted) controller.abort(options.signal.reason);
    else options.signal.addEventListener('abort', () => controller.abort(options.signal?.reason), { once: true });
  }

  const work = typeof input === 'function' ? input(controller.signal) : input;

  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      controller.abort();
      reject(options.error ?? new TimeoutError(`Operation timed out after ${ms}ms`));
    }, ms);

    work.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error instanceof Error ? error : new Error(String(error)));
      },
    );
  });
}

/** Options for {@link pMap}. */
export interface PMapOptions {
  /** Maximum number of concurrently running mappers. Default `Infinity`. */
  readonly concurrency?: number;
  /**
   * When `false`, collect all settled outcomes and reject with an
   * `AggregateError` only after every item completes. Default `true`.
   */
  readonly stopOnError?: boolean;
  /** Abort remaining work. In-flight mappers should observe the signal. */
  readonly signal?: AbortSignal;
}

/**
 * Map over an iterable with bounded concurrency, preserving input order in the
 * output array. The mapper receives the element and its index.
 */
export async function pMap<T, R>(
  items: Iterable<T>,
  mapper: (item: T, index: number) => Promise<R> | R,
  options: PMapOptions = {},
): Promise<R[]> {
  const { concurrency = Number.POSITIVE_INFINITY, stopOnError = true, signal } = options;
  if (concurrency < 1) throw new TypeError('pMap: concurrency must be at least 1');

  const entries = Array.from(items);
  const results: R[] = new Array<R>(entries.length);
  const errors: unknown[] = [];
  let cursor = 0;

  const limit = Math.min(concurrency, entries.length) || 0;

  const worker = async (): Promise<void> => {
    while (cursor < entries.length) {
      if (signal?.aborted) throw toAbortError(signal);
      const index = cursor;
      cursor += 1;
      try {
        results[index] = await mapper(entries[index] as T, index);
      } catch (error) {
        if (stopOnError) throw error;
        errors.push(error);
      }
    }
  };

  const workers = Array.from({ length: limit }, () => worker());
  await Promise.all(workers);

  if (errors.length > 0) {
    throw new AggregateError(errors, `pMap: ${errors.length} of ${entries.length} items failed`);
  }

  return results;
}

function toAbortError(signal?: AbortSignal): Error {
  const reason = signal?.reason;
  if (reason instanceof Error) return reason;
  return new AbortError(typeof reason === 'string' ? reason : undefined);
}
